import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';

/**
 * Lightweight paginated table (MUI Core only — no MUI X / license).
 * Column defs mirror DataGrid basics: field, headerName, width, minWidth, flex, align, headerAlign, renderCell.
 */
export default function InsightDataTable({
  rows = [],
  columns = [],
  initialPageSize = 25,
  pageSizeOptions = [10, 25, 50],
  dense = true,
  headerBg,
  headerBorder,
  /** Called with the full row object when the row is clicked (links inside cells do not trigger). */
  onRowClick,
  sx,
}) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    setPage(0);
  }, [rows.length]);

  const pageRows = useMemo(() => {
    const start = page * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const renderCellContent = (col, row) => {
    const value = row[col.field];
    if (typeof col.renderCell === 'function') {
      return col.renderCell({ value, row, id: row.id });
    }
    if (value !== null && value !== undefined && typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return value ?? '';
  };

  const stretchTable = columns.some((col) => col.flex);
  const cellSx = (col) => ({
    minWidth: col.minWidth ?? col.width,
    width: col.width,
    maxWidth: col.maxWidth ?? (col.flex ? 560 : undefined),
    py: dense ? 0.5 : 1,
    fontSize: dense ? '0.8125rem' : undefined,
    verticalAlign: 'top',
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        ...sx,
      }}
    >
      <TableContainer sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Table
          size={dense ? 'small' : 'medium'}
          stickyHeader
          sx={{
            width: stretchTable ? '100%' : 'max-content',
            tableLayout: stretchTable ? 'auto' : 'fixed',
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  align={col.headerAlign || 'left'}
                  sx={{
                    ...cellSx(col),
                    backgroundColor: headerBg,
                    borderBottom: headerBorder ? `2px solid ${headerBorder}` : undefined,
                    fontWeight: 600,
                    whiteSpace: col.maxWidth ? 'normal' : undefined,
                    lineHeight: col.maxWidth ? 1.25 : undefined,
                    wordBreak: col.maxWidth ? 'break-word' : undefined,
                  }}
                >
                  {col.headerName ?? col.field}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row) => (
              <TableRow
                key={row.id ?? JSON.stringify(row)}
                hover={Boolean(onRowClick)}
                onClick={
                  onRowClick
                    ? (e) => {
                        if (e.target.closest?.('a')) return;
                        onRowClick(row);
                      }
                    : undefined
                }
                sx={
                  onRowClick
                    ? { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }
                    : undefined
                }
              >
                {columns.map((col) => (
                  <TableCell key={col.field} align={col.align || 'left'} sx={cellSx(col)}>
                    {renderCellContent(col, row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={pageSizeOptions}
        sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}
      />
    </Box>
  );
}

export { InsightDataTable };
/** Same component — familiar name for migrations from MUI X */
export const DataGrid = InsightDataTable;
